import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormControlLabel,
  Switch,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import api from "../../services/api"
import { Partner } from "../../interfaces"
import { useCryptoNavigation, useAppNavigation } from "../../App"
import HorseLoader from "../../components/loadingHorse"
import { useIsMobile } from "../../hooks/useMediaQuery"
import { supportedLanguages } from "../../i18n"
import { getScope } from "../../hooks/useScope"
import PhoneUtils from "../../utils/phoneUtils"
import {
  formatFiscalCode,
  stripFiscalMask,
  validateFiscalCode,
  formatZipcode,
  exemploCodigoPostal,
  maxLengthCodigoPostal,
  codigoPostalValido,
  normalizarCodigoPostal,
  descreverFormatoFiscal,
} from "../../utils/fiscalCode"
import {
  prepararLogo,
  logoParaDataUri,
  LogoInvalidaError,
  LOGO_REQUISITOS,
} from "../../utils/partnerLogo"

/**
 * Cadastro de parceiro.
 *
 * Nasceu do TenantForm, mas sem três vícios dele:
 *  - o número fiscal não é mais CNPJ cravado (o `removeMask` antigo fazia
 *    `replace(/\D/g,'')`, que destrói número fiscal com letras);
 *  - o telefone passa pela mesma validação do cadastro de usuário, que antes
 *    só existia lá dentro;
 *  - o CEP respeita o país.
 */
export default function PartnerForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  const isMobile = useIsMobile()
  const inputArquivo = useRef<HTMLInputElement | null>(null)

  const id = getParam("id")
  const mode = getParam("mode")
  const isEditMode = mode === "edit" && Boolean(id)

  const scope = getScope()
  const somenteLeitura = !scope.canManagePartners

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoInfo, setLogoInfo] = useState<string | null>(null)

  const [formData, setFormData] = useState<Partner>({
    partnerId: undefined,
    partnerName: "",
    partnerNickName: "",
    partnerPhone: "",
    partnerAddress: "",
    partnerCity: "",
    partnerState: "",
    partnerZipcode: "",
    partnerCountry: "",
    partnerFiscalCode: "",
    partnerLanguage: null,
    partnerLogo: null,
    partnerStatus: 1,
  })

  // ── Carga em edição ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id) return
    setLoading(true)

    api
      .get(`/partner/${id}`)
      .then((response) => {
        // Envelope tolerante: a rota pode devolver {partner}, {partners:[...]}
        // ou o objeto cru. Formato inesperado vira erro explícito, não uma
        // tela preenchida pela metade.
        const corpo = response.data
        const data =
          corpo?.partner ??
          (Array.isArray(corpo?.partners) ? corpo.partners[0] : null) ??
          (corpo && typeof corpo === "object" && "partnerName" in corpo
            ? corpo
            : null)

        if (!data) {
          console.error("Resposta inesperada de /partner/" + id, corpo)
          setError(
            `${t("partners.loadError")} (resposta sem os dados do parceiro)`,
          )
          return
        }

        setFormData({
          ...data,
          partnerFiscalCode: data.partnerFiscalCode
            ? formatFiscalCode(data.partnerFiscalCode, data.partnerCountry)
            : "",
          partnerZipcode: data.partnerZipcode
            ? formatZipcode(data.partnerZipcode, data.partnerCountry)
            : "",
          partnerStatus: data.partnerStatus ?? 1,
          partnerLanguage: data.partnerLanguage ?? null,
        })
        setLogoPreview(logoParaDataUri(data.partnerLogo))
      })
      .catch((err) => {
        console.error("Erro ao carregar parceiro:", err)
        // Diz O QUE falhou. "Tente novamente" sem causa não ajuda ninguém a
        // saber se é id errado, rota ausente ou sessão expirada.
        const status = err?.response?.status
        const detalhe = status
          ? `HTTP ${status} em /partner/${id}`
          : err?.message || "sem resposta do servidor"
        setError(`${t("partners.loadError")} (${detalhe})`)
      })
      .finally(() => setLoading(false))
  }, [isEditMode, id, t])

  // ── Entrada ───────────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    let formatado = value

    if (name === "partnerId") {
      formatado = value.replace(/\D/g, "")
    } else if (name === "partnerFiscalCode") {
      formatado = formatFiscalCode(value, formData.partnerCountry)
    } else if (name === "partnerZipcode") {
      formatado = formatZipcode(value, formData.partnerCountry)
    } else if (name === "partnerPhone") {
      formatado = PhoneUtils.formatPhone(value)
    } else if (name === "partnerCountry") {
      formatado = value.toUpperCase().slice(0, 2)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formatado,
      // Trocar o PAÍS muda a máscara: reformata o código postal já digitado,
      // senão ele fica com a pontuação do país anterior.
      ...(name === "partnerCountry"
        ? { partnerZipcode: formatZipcode(prev.partnerZipcode || "", formatado) }
        : {}),
    }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }))
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value === "" ? null : value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }))
  }

  // ── Logo ──────────────────────────────────────────────────────────────────
  const handleLogoEscolhida = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const arquivo = e.target.files?.[0]
    e.target.value = "" // permite reescolher o mesmo arquivo
    if (!arquivo) return

    setErrors((prev) => ({ ...prev, partnerLogo: "" }))
    try {
      const pronta = await prepararLogo(arquivo)
      setFormData((prev) => ({ ...prev, partnerLogo: pronta.base64 }))
      setLogoPreview(pronta.dataUri)
      setLogoInfo(
        `${pronta.largura}×${pronta.altura} px · ${Math.round(pronta.bytes / 1024)} KB`,
      )
    } catch (err) {
      const msg =
        err instanceof LogoInvalidaError ? err.message : t("partners.logoInvalid")
      setErrors((prev) => ({ ...prev, partnerLogo: msg }))
      setLogoInfo(null)
    }
  }

  const removerLogo = () => {
    setFormData((prev) => ({ ...prev, partnerLogo: null }))
    setLogoPreview(null)
    setLogoInfo(null)
  }

  // ── Validação ─────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const novos: Record<string, string> = {}

    if (!isEditMode) {
      if (formData.partnerId === undefined || formData.partnerId === null || String(formData.partnerId) === "") {
        novos.partnerId = t("partners.idRequired")
      } else if (Number(formData.partnerId) < 0) {
        novos.partnerId = t("partners.idPositive")
      }
    }
    if (!formData.partnerName?.trim()) novos.partnerName = t("partners.nameRequired")
    if (!formData.partnerNickName?.trim())
      novos.partnerNickName = t("partners.nickNameRequired")

    if (!formData.partnerPhone?.trim()) {
      novos.partnerPhone = t("partners.phoneRequired")
    } else if (!PhoneUtils.validatePhone(formData.partnerPhone)) {
      novos.partnerPhone = PhoneUtils.getErrorMessage(formData.partnerPhone)
    }

    if (!formData.partnerAddress?.trim())
      novos.partnerAddress = t("partners.addressRequired")
    if (!formData.partnerCity?.trim()) novos.partnerCity = t("partners.cityRequired")
    if (!formData.partnerState?.trim())
      novos.partnerState = t("partners.stateRequired")
    if (!formData.partnerZipcode?.trim())
      novos.partnerZipcode = t("partners.zipcodeRequired")
    else if (!codigoPostalValido(formData.partnerZipcode, formData.partnerCountry))
      novos.partnerZipcode = t("tenants.zipcodeFormat", {
        mask: exemploCodigoPostal(formData.partnerCountry),
      })

    if (!formData.partnerCountry?.trim()) {
      novos.partnerCountry = t("partners.countryRequired")
    } else if (formData.partnerCountry.trim().length !== 2) {
      novos.partnerCountry = t("partners.countryLength")
    }

    const fiscal = validateFiscalCode(
      formData.partnerFiscalCode || "",
      formData.partnerCountry,
    )
    if (!fiscal.valid) {
      const base = t(
        (fiscal.errorKey || "tenants.fiscalCodeRequired").replace(
          "tenants.",
          "partners.",
        ),
      )
      novos.partnerFiscalCode = fiscal.esperado
        ? `${base} (${fiscal.esperado})`
        : base
    }

    setErrors(novos)
    setError(Object.keys(novos).length ? t("partners.requiredFieldsError") : null)
    return Object.keys(novos).length === 0
  }

  // ── Gravação ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) return
    setSaving(true)
    setError(null)

    try {
      const dataToSend: Record<string, any> = {
        ...formData,
        partnerId: isEditMode ? Number(id) : Number(formData.partnerId),
        // stripFiscalMask preserva LETRAS — ao contrário do removeMask antigo.
        partnerFiscalCode: stripFiscalMask(formData.partnerFiscalCode || ""),
        partnerZipcode: normalizarCodigoPostal(
          formData.partnerZipcode,
          formData.partnerCountry,
        ),
      }

      if (isEditMode) {
        await api.put(`/partner/${id}`, dataToSend)
      } else {
        await api.post("/partner", dataToSend)
      }
      navigateToPage("partners", { mode: "list" })
    } catch (err: any) {
      console.error("Erro ao salvar parceiro:", err)
      setError(err?.response?.data?.message || t("partners.saveError"))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => navigateToPage("partners", { mode: "list" })

  if (loading) return <HorseLoader />

  return (
    <Paper sx={{ p: isMobile ? 2 : 3, m: isMobile ? 1 : 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {isEditMode ? t("partners.editTitle") : t("partners.newTitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            name="partnerId"
            label={t("partners.idLabel")}
            value={formData.partnerId ?? ""}
            onChange={handleInputChange}
            disabled={isEditMode || somenteLeitura}
            error={Boolean(errors.partnerId)}
            helperText={
              errors.partnerId ||
              (isEditMode ? t("partners.idLockedHelper") : t("partners.idUniqueHelper"))
            }
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            required
            name="partnerName"
            label={t("fields.name")}
            value={formData.partnerName || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerName)}
            helperText={errors.partnerName}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            name="partnerNickName"
            label={t("fields.nickname")}
            value={formData.partnerNickName || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerNickName)}
            helperText={errors.partnerNickName}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            name="partnerPhone"
            label={t("fields.phone")}
            value={formData.partnerPhone || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerPhone)}
            helperText={errors.partnerPhone}
            inputProps={{ maxLength: 20 }}
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            required
            name="partnerAddress"
            label={t("fields.address")}
            value={formData.partnerAddress || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerAddress)}
            helperText={errors.partnerAddress}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            name="partnerZipcode"
            label={t("fields.zipcode")}
            value={formData.partnerZipcode || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerZipcode)}
            helperText={
              errors.partnerZipcode ||
              t("common.formatLabel", {
                mask: exemploCodigoPostal(formData.partnerCountry),
              })
            }
            placeholder={exemploCodigoPostal(formData.partnerCountry)}
            inputProps={{ maxLength: maxLengthCodigoPostal(formData.partnerCountry) }}
          />
        </Grid>

        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            required
            name="partnerCity"
            label={t("fields.city")}
            value={formData.partnerCity || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerCity)}
            helperText={errors.partnerCity}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            name="partnerState"
            label={t("fields.state")}
            value={formData.partnerState || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerState)}
            helperText={errors.partnerState}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            required
            name="partnerCountry"
            label={t("fields.country")}
            value={formData.partnerCountry || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerCountry)}
            helperText={errors.partnerCountry || t("partners.countryHelper")}
            inputProps={{ maxLength: 2 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            name="partnerFiscalCode"
            label={t("fields.fiscalCode")}
            value={formData.partnerFiscalCode || ""}
            onChange={handleInputChange}
            disabled={somenteLeitura}
            error={Boolean(errors.partnerFiscalCode)}
            helperText={
              errors.partnerFiscalCode ||
              descreverFormatoFiscal(formData.partnerCountry) ||
              undefined
            }
            inputProps={{ maxLength: 24 }}
          />
        </Grid>

        {/* Idioma: nível mais alto da cascata (parceiro > proprietário > usuário). */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth disabled={somenteLeitura}>
            <InputLabel id="partner-language-label">
              {t("common.language")}
            </InputLabel>
            <Select
              labelId="partner-language-label"
              name="partnerLanguage"
              label={t("common.language")}
              value={formData.partnerLanguage ?? ""}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {supportedLanguages.map((l) => (
                <MenuItem key={l.code} value={l.code}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("partners.languageHelper")}</FormHelperText>
          </FormControl>
        </Grid>

        {/* Logo: exibida na home do app. */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              alignItems: isMobile ? "flex-start" : "center",
              p: 2,
              border: "1px dashed",
              borderColor: errors.partnerLogo ? "error.main" : "divider",
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                width: 180,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                borderRadius: 1,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt={t("fields.logo")}
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              ) : (
                <ImageIcon color="disabled" />
              )}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">{t("fields.logo")}</Typography>
              <Typography
                variant="caption"
                color={errors.partnerLogo ? "error" : "text.secondary"}
                sx={{ display: "block" }}
              >
                {errors.partnerLogo || logoInfo || t("partners.logoHelper")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <input
                ref={inputArquivo}
                type="file"
                hidden
                accept={LOGO_REQUISITOS.tiposAceitos.join(",")}
                onChange={handleLogoEscolhida}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={somenteLeitura}
                onClick={() => inputArquivo.current?.click()}
              >
                {t("common.select")}
              </Button>
              {logoPreview && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={somenteLeitura}
                  onClick={removerLogo}
                >
                  {t("common.remove")}
                </Button>
              )}
            </Box>
          </Box>
        </Grid>

        {isEditMode && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.partnerStatus !== 0}
                  // Ligar/desligar um parceiro é só do super admin — quem o
                  // criou e o gere. Editar os demais campos é mais permissivo.
                  disabled={somenteLeitura || !scope.canTogglePartnerStatus}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      partnerStatus: e.target.checked ? 1 : 0,
                    }))
                  }
                />
              }
              label={t("fields.enabled")}
            />
            {!scope.canTogglePartnerStatus && (
              <FormHelperText>{t("partners.toggleRestricted")}</FormHelperText>
            )}
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", gap: 1, mt: 3, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          disabled={saving}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || somenteLeitura}
        >
          {t("common.save")}
        </Button>
      </Box>
    </Paper>
  )
}
